/**
 * MenuController - Generic dropdown menu management
 * 
 * Provides a factory for creating consistent dropdown menus with:
 * - Toggle open/close
 * - Close on outside click
 * - Close other menus when opening
 * - Optional callbacks for open/close events
 */

// Registry of all active dropdown menus for cross-closing
const menuRegistry = new Map();

/**
 * Create a dropdown menu controller
 * @param {Object} config - Menu configuration
 * @param {string} config.triggerId - ID of the trigger element (click target container)
 * @param {string} config.dropdownId - ID of the dropdown element
 * @param {string[]} [config.closeOthers] - Array of menu names to close when this opens
 * @param {Function} [config.onOpen] - Callback when menu opens
 * @param {Function} [config.onClose] - Callback when menu closes
 * @param {string} [config.name] - Name for registry (defaults to dropdownId)
 * @returns {Object} Menu controller with toggle, open, close methods
 */
export function createDropdownMenu(config) {
    const {
        triggerId,
        dropdownId,
        closeOthers = [],
        onOpen = null,
        onClose = null,
        name = dropdownId
    } = config;
    
    let outsideClickHandler = null;
    
    function getDropdown() {
        return document.getElementById(dropdownId);
    }
    
    function getTrigger() {
        return document.getElementById(triggerId);
    }
    
    function isOpen() {
        const dropdown = getDropdown();
        return dropdown?.classList.contains('open') || false;
    }
    
    function closeOtherMenus() {
        closeOthers.forEach(menuName => {
            const menu = menuRegistry.get(menuName);
            if (menu && menu !== controller) {
                menu.close();
            }
        });
    }
    
    function handleOutsideClick(event) {
        const trigger = getTrigger();
        const dropdown = getDropdown();
        
        if (!trigger || !dropdown) return;
        
        // If click is outside the trigger area, close the menu
        if (!trigger.contains(event.target)) {
            close();
        }
    }
    
    function open() {
        const dropdown = getDropdown();
        if (!dropdown || isOpen()) return;
        
        // Close other menus first
        closeOtherMenus();
        
        // Open this menu
        dropdown.classList.add('open');
        
        // Call onOpen callback
        if (onOpen) {
            onOpen();
        }
        
        // Add outside click listener after a short delay
        // (to prevent the opening click from immediately closing)
        setTimeout(() => {
            outsideClickHandler = handleOutsideClick;
            document.addEventListener('click', outsideClickHandler, { once: true });
        }, 0);
    }
    
    function close() {
        const dropdown = getDropdown();
        if (!dropdown) return;
        
        const wasOpen = isOpen();
        dropdown.classList.remove('open');
        
        // Remove outside click listener if it exists
        if (outsideClickHandler) {
            document.removeEventListener('click', outsideClickHandler);
            outsideClickHandler = null;
        }
        
        // Call onClose callback
        if (wasOpen && onClose) {
            onClose();
        }
    }
    
    function toggle(event) {
        if (event) {
            event.stopPropagation();
        }
        
        if (isOpen()) {
            close();
        } else {
            open();
        }
    }
    
    const controller = {
        toggle,
        open,
        close,
        isOpen
    };
    
    // Register in the global registry
    menuRegistry.set(name, controller);
    
    return controller;
}

/**
 * Close all registered menus
 */
export function closeAllMenus() {
    menuRegistry.forEach(menu => menu.close());
}

/**
 * Get a registered menu by name
 * @param {string} name - Menu name
 * @returns {Object|undefined} Menu controller
 */
export function getMenu(name) {
    return menuRegistry.get(name);
}

// ============= Pre-configured menu instances =============
// These are created lazily on first access to ensure DOM is ready

let accountMenu = null;
let patchSwitcherMenu = null;
let fileMenu = null;
let userMenu = null;

/**
 * Initialize the account menu
 * @param {Object} options - Additional options
 * @param {Function} [options.onOpen] - Called when menu opens
 * @returns {Object} Menu controller
 */
export function initAccountMenu(options = {}) {
    if (!accountMenu) {
        accountMenu = createDropdownMenu({
            triggerId: 'account-menu',
            dropdownId: 'account-dropdown',
            closeOthers: ['patch-switcher-dropdown', 'file-menu-dropdown'],
            name: 'account-dropdown',
            ...options
        });
    }
    return accountMenu;
}

/**
 * Initialize the patch switcher menu
 * @param {Object} options - Additional options
 * @param {Function} [options.onOpen] - Called when menu opens (e.g., to render patch list)
 * @returns {Object} Menu controller
 */
export function initPatchSwitcherMenu(options = {}) {
    if (!patchSwitcherMenu) {
        patchSwitcherMenu = createDropdownMenu({
            triggerId: 'patch-name-area',
            dropdownId: 'patch-switcher-dropdown',
            closeOthers: ['account-dropdown', 'file-menu-dropdown'],
            name: 'patch-switcher-dropdown',
            ...options
        });
    }
    return patchSwitcherMenu;
}

/**
 * Initialize the file menu (legacy, may not exist in DOM)
 * @param {Object} options - Additional options
 * @returns {Object} Menu controller
 */
export function initFileMenu(options = {}) {
    if (!fileMenu) {
        fileMenu = createDropdownMenu({
            triggerId: 'file-menu',
            dropdownId: 'file-menu-dropdown',
            closeOthers: ['patch-switcher-dropdown', 'account-dropdown'],
            name: 'file-menu-dropdown',
            ...options
        });
    }
    return fileMenu;
}

/**
 * Initialize the user menu
 * @param {Object} options - Additional options
 * @returns {Object} Menu controller
 */
export function initUserMenu(options = {}) {
    if (!userMenu) {
        userMenu = createDropdownMenu({
            triggerId: 'user-menu',
            dropdownId: 'user-menu-dropdown',
            name: 'user-menu-dropdown',
            ...options
        });
    }
    return userMenu;
}

// ============= Convenience exports for direct use =============

export function toggleAccountMenu(event) {
    initAccountMenu().toggle(event);
}

export function closeAccountMenu() {
    if (accountMenu) accountMenu.close();
}

export function togglePatchSwitcher(event, onOpen) {
    const menu = initPatchSwitcherMenu({ onOpen });
    menu.toggle(event);
}

export function closePatchSwitcher() {
    if (patchSwitcherMenu) patchSwitcherMenu.close();
}

export function toggleFileMenu(event) {
    initFileMenu().toggle(event);
}

export function closeFileMenu() {
    if (fileMenu) fileMenu.close();
}

export function toggleUserMenu(event) {
    initUserMenu().toggle(event);
}

export function closeUserMenu() {
    if (userMenu) userMenu.close();
}
